import { paymentFaultOf, upstreamField } from '../domain/errors';
import {
  appliedPlanChange,
  type MembershipManageError,
  type MembershipManageFailure,
  type MembershipManageIncident,
  type MembershipManageOperation,
} from '../domain/manage';
import type { MembershipNotice } from '../domain/purchase';
import type { Membership } from '../domain/types';

/** What the member card does about a failed cancel, plan switch or "Keep current plan". */
export interface ManageErrorOutcome {
  /** Sentry incident; `null` for 401, 404 and 409, which are not reported. */
  incident: MembershipManageIncident | null;
  /**
   * `reload`: the membership changed under us — re-read it in place and close
   * the sheet; `reconcile`: ask `/me` whether it went through; `fail`: show
   * the failure. Nothing is ever re-sent.
   */
  next: 'reload' | 'reconcile' | 'fail';
  reason: MembershipManageFailure['reason'];
  /**
   * Shown when `reconcile` finds `/me` unchanged: `confirming` for a plan
   * switch whose payment outcome is unknown (a 503), `failed` otherwise.
   */
  unsettledReason: MembershipManageFailure['reason'];
  /** The zone's plans are stale too. */
  reloadPlans: boolean;
  /** Banner shown after the reload. */
  notice: MembershipNotice | null;
}

const quietReload = (
  overrides: Partial<ManageErrorOutcome> = {},
): ManageErrorOutcome => ({
  incident: null,
  next: 'reload',
  reason: 'failed',
  unsettledReason: 'failed',
  reloadPlans: false,
  notice: null,
  ...overrides,
});

const failed = (
  incident: MembershipManageIncident | null,
  reason: ManageErrorOutcome['reason'] = 'failed',
): ManageErrorOutcome => ({
  incident,
  next: 'fail',
  reason,
  unsettledReason: 'failed',
  reloadPlans: false,
  notice: null,
});

/**
 * Classifies a failed `/me/cancel`, `/me/change-plan` or
 * `/me/change-plan/cancel`. A refused card means something only for a plan
 * switch (the upgrade charge); on any other request it is a plain failure.
 * So does a payment the service refused: a switch fails at once, since
 * nothing was charged, while a refusal on a request without a payment is
 * checked through `/me` like any other lost answer.
 * `MEMBERSHIP_UPGRADE_REQUIRES_PAYMENT` on a switch keeps the sheet open with
 * "try again" instead of a silent reload — TODO(JS-14485): replace with the
 * payment step once the backend says how an upgrade asks for one (question 1
 * of the spec).
 */
export const manageErrorOutcome = (
  operation: MembershipManageOperation,
  error: MembershipManageError,
): ManageErrorOutcome => {
  const { kind, status, code, upstream } = error;
  const reported: MembershipManageIncident = {
    reason: 'manageFailed',
    operation,
    kind,
    status,
    code,
    ...upstreamField(upstream),
  };
  switch (kind) {
    case 'unknown_outcome':
      return {
        ...failed(reported),
        next: 'reconcile',
        unsettledReason:
          operation === 'changePlan' &&
          paymentFaultOf(status, code) === 'unknown'
            ? 'confirming'
            : 'failed',
      };
    case 'payment_rejected':
      return operation === 'changePlan'
        ? failed(reported, 'rejected')
        : { ...failed(reported), next: 'reconcile' };
    case 'card_refused':
      return operation === 'changePlan'
        ? failed(
            { reason: 'cardRefused', flow: 'planChange', status, code },
            'declined',
          )
        : failed(reported);
    case 'plan_change_not_applicable':
      return operation === 'changePlan'
        ? quietReload({
            reloadPlans: true,
            notice: { kind: 'planUnavailable' },
          })
        : quietReload();
    case 'upgrade_requires_payment':
      return operation === 'changePlan' ? failed(null) : quietReload();
    case 'not_found':
    case 'not_cancellable':
    case 'conflict':
      return quietReload();
    case 'unauthorized':
      return failed(null);
    default:
      return failed(reported);
  }
};

/**
 * Whether `/me` shows that a request whose answer was lost went through: the
 * membership is cancelled, switched or scheduled to the target plan, or no
 * longer has a scheduled switch.
 */
export const isManageSettled = (
  operation: MembershipManageOperation,
  membership: Membership | null,
  targetPlanId: string | null,
): membership is Membership => {
  if (!membership) {
    return false;
  }
  switch (operation) {
    case 'cancel':
      return membership.status === 'cancelled';
    case 'changePlan':
      return (
        targetPlanId !== null &&
        appliedPlanChange(membership, targetPlanId) !== null
      );
    case 'keepPlan':
      return membership.scheduledChange === null;
  }
};
